'use client';

import { MaterialReactTable, type MRT_ColumnDef, type MRT_SortingState } from 'material-react-table';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import { useNotification } from '@/contexts/NotificationContext';
import {
  queryMachineInventoryBalances,
  queryTomVendingInventory,
  type MachineBalanceApiRow,
  type TomVendingInventoryRow,
  upsertInventoryBalanceByMachine,
} from '@/hooks/useTomVendingInventory';

const PAGE_SIZE = 25;

function ledgerBookQuantity(row: MachineBalanceApiRow): number {
  return row.ledgerQuantity ?? row.quantity;
}

interface TomsVendingMachineInventorySectionProps {
  locationId: number;
  machineId: number;
  /** Called after quantity changes so parent views (e.g. location “All” totals) can refresh. */
  onBalancesChanged?: () => void;
}

const TomsVendingMachineInventorySection: React.FC<TomsVendingMachineInventorySectionProps> = ({
  locationId,
  machineId,
  onBalancesChanged,
}) => {
  const [data, setData] = useState<MachineBalanceApiRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'id', desc: false }]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<MachineBalanceApiRow | null>(null);
  const [editQty, setEditQty] = useState('0');

  const [addOpen, setAddOpen] = useState(false);
  const [comboInput, setComboInput] = useState('');
  const [comboOptions, setComboOptions] = useState<TomVendingInventoryRow[]>([]);
  const [comboLoading, setComboLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TomVendingInventoryRow | null>(null);
  const [addQty, setAddQty] = useState('1');

  const [removeRow, setRemoveRow] = useState<MachineBalanceApiRow | null>(null);

  const { showSuccess, showError } = useNotification();

  const qtyOnMachineByItemId = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of data) {
      m.set(r.inventoryItem.id, ledgerBookQuantity(r));
    }
    return m;
  }, [data]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const sortId = sorting[0]?.id || 'id';
      const orderBy = sortId === 'quantity' ? 'id' : sortId;
      const r = await queryMachineInventoryBalances({
        vendingMachineId: machineId,
        locationId,
        effectiveOpenPendingAcrossLocation: true,
        hideZeroQuantity: true,
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search: debouncedSearch,
        orderBy,
        orderDirection: sorting[0]?.desc ? 'DESC' : 'ASC',
      });
      setData(r.data);
      setTotalRows(r.total);
    } catch (e: any) {
      showError(e?.message || 'Failed to load');
      setData([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    machineId,
    locationId,
    pagination,
    sorting,
    debouncedSearch,
    tableRefreshKey,
    showError,
  ]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!addOpen) {
      setComboInput('');
      setSelectedItem(null);
      setComboOptions([]);
      setAddQty('1');
      return;
    }
    let ok = true;
    setComboLoading(true);
    const t = window.setTimeout(() => {
      queryTomVendingInventory({
        skip: 0,
        take: 50,
        search: comboInput.trim(),
        orderBy: 'upc',
        orderDirection: 'ASC',
      })
        .then((res) => {
          if (ok) setComboOptions(res.data);
        })
        .catch(() => {
          if (ok) setComboOptions([]);
        })
        .finally(() => {
          if (ok) setComboLoading(false);
        });
    }, 300);
    return () => {
      ok = false;
      window.clearTimeout(t);
    };
  }, [addOpen, comboInput]);

  const openEdit = useCallback((row: MachineBalanceApiRow) => {
    setEditing(row);
    setEditQty(String(ledgerBookQuantity(row)));
    setEditOpen(true);
  }, []);

  const saveEdit = async () => {
    if (!editing) return;
    const n = parseInt(editQty, 10);
    if (Number.isNaN(n) || n < 0) {
      showError('Invalid quantity');
      return;
    }
    try {
      await upsertInventoryBalanceByMachine({
        vendingMachineId: machineId,
        inventoryItemId: editing.inventoryItem.id,
        quantity: n,
        locationId,
      });
      showSuccess('Updated');
      setEditOpen(false);
      setEditing(null);
      setTableRefreshKey((k) => k + 1);
      onBalancesChanged?.();
    } catch (e: any) {
      showError(e?.message || 'Save failed');
    }
  };

  const saveAdd = async () => {
    if (!selectedItem) {
      showError('Select a catalog line (UPC)');
      return;
    }
    const n = parseInt(addQty, 10);
    if (Number.isNaN(n) || n < 0) {
      showError('Invalid quantity');
      return;
    }
    try {
      await upsertInventoryBalanceByMachine({
        vendingMachineId: machineId,
        inventoryItemId: selectedItem.id,
        quantity: n,
        locationId,
      });
      showSuccess('Saved');
      setAddOpen(false);
      setTableRefreshKey((k) => k + 1);
      onBalancesChanged?.();
    } catch (e: any) {
      showError(e?.message || 'Save failed');
    }
  };

  const confirmRemove = async () => {
    if (!removeRow) return;
    try {
      await upsertInventoryBalanceByMachine({
        vendingMachineId: machineId,
        inventoryItemId: removeRow.inventoryItem.id,
        quantity: 0,
        locationId,
      });
      showSuccess('Removed from machine');
      setRemoveRow(null);
      setTableRefreshKey((k) => k + 1);
      onBalancesChanged?.();
    } catch (e: any) {
      showError(e?.message || 'Remove failed');
    }
  };

  const columns = useMemo<MRT_ColumnDef<MachineBalanceApiRow>[]>(
    () => [
      {
        id: 'thumbnail',
        header: '',
        size: 56,
        enableSorting: false,
        Cell: ({ row }) => {
          const inv = row.original.inventoryItem;
          const src = inv.thumbnailUrl || inv.imageUrl;
          return src ? (
            <Avatar src={src} variant="rounded" sx={{ width: 40, height: 40 }} />
          ) : (
            <Avatar variant="rounded" sx={{ width: 40, height: 40 }}>
              <Inventory2Icon fontSize="small" />
            </Avatar>
          );
        },
      },
      { accessorFn: (row) => row.inventoryItem.upc, header: 'UPC', id: 'upc' },
      {
        accessorFn: (row) => row.inventoryItem.name,
        header: 'Name',
        id: 'name',
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        id: 'quantity',
        accessorFn: (row) => row.quantity,
        header: 'Qty',
        size: 110,
        enableSorting: false,
        Cell: ({ row }) => {
          const ledger = ledgerBookQuantity(row.original);
          const eff = row.original.quantity;
          return (
            <Box>
              <Typography variant="body2">{eff}</Typography>
              {ledger !== eff ? (
                <Typography variant="caption" color="text.secondary" component="span" display="block">
                  Ledger {ledger}
                  {row.original.pending != null ? ' (pending visit)' : ''}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 96,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex' }}>
            <IconButton size="small" onClick={() => openEdit(row.original)} aria-label="Edit quantity">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setRemoveRow(row.original)}
              aria-label="Remove from machine"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [openEdit],
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Quantities reflect open site-visit pending when applicable; editing adjusts <strong>ledger</strong>{' '}
          (booked) stock — not projected pending tallies.
        </Typography>
        <Button variant="contained" onClick={() => setAddOpen(true)} sx={{ textTransform: 'none' }}>
          + Add item
        </Button>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={data}
        manualPagination
        manualSorting
        enableGlobalFilter={false}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        rowCount={totalRows}
        state={{ isLoading, pagination, sorting }}
        initialState={{ density: 'compact' }}
        renderTopToolbarCustomActions={() => (
          <TextField
            placeholder="Search UPC, name…"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            sx={{ width: 280 }}
          />
        )}
      />

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Quantity</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {editing?.inventoryItem.upc} — {editing?.inventoryItem.name || '—'}
          </Typography>
          {editing && ledgerBookQuantity(editing) !== editing.quantity ? (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Projected on machine now: {editing.quantity} — you are editing ledger {ledgerBookQuantity(editing)}.
            </Typography>
          ) : null}
          <TextField
            label="Quantity"
            type="number"
            size="small"
            fullWidth
            slotProps={{ htmlInput: { min: 0 } }}
            value={editQty}
            onChange={(e) => setEditQty(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveEdit()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add catalog item by UPC</DialogTitle>
        <DialogContent>
          <Autocomplete<TomVendingInventoryRow>
            options={comboOptions}
            loading={comboLoading}
            filterOptions={(x) => x}
            inputValue={comboInput}
            onInputChange={(_, v) => setComboInput(v)}
            value={selectedItem}
            onChange={(_, v) => setSelectedItem(v)}
            getOptionLabel={(o) => `${o.upc} — ${o.name?.trim() || '—'}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, option) => {
              const onM = qtyOnMachineByItemId.get(option.id);
              return (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">
                      {option.upc} — {option.name?.trim() || '—'}
                    </Typography>
                    {onM !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        On this machine now: {onM}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Type UPC or product name"
                placeholder="Search catalog…"
                sx={{ mt: 1 }}
              />
            )}
          />
          <TextField
            label="Quantity"
            type="number"
            size="small"
            fullWidth
            slotProps={{ htmlInput: { min: 0 } }}
            value={addQty}
            onChange={(e) => setAddQty(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveAdd()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={removeRow != null} onClose={() => setRemoveRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove from machine</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{removeRow?.inventoryItem.upc}</strong>
            {removeRow?.inventoryItem.name ? ` (${removeRow.inventoryItem.name})` : ''} from this machine? Stock
            quantity will be set to zero and the movement will appear in stock history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveRow(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void confirmRemove()}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TomsVendingMachineInventorySection;
